// Client-side routing
import { eventBus } from './eventBus.js';
import { getLocaleFromPath, localizedPath, setLocale, stripLocaleFromPath } from '@lib/i18n/i18n.js';
import { tracker } from '@services/analytics/tracker.js';

class Router {
  constructor() {
    this._routes = new Map();
    this._currentRoute = null;
    this._rootEl = null;
  }

  /**
   * Add a route
   * @param {string} path - Route path
   * @param {Function} loader - Lazy loader function that returns the page module
   */
  addRoute(path, loader) {
    this._routes.set(path, loader);
  }

  /**
   * Navigate to a path
   * @param {string} path - Target path
   * @param {Object} state - History state
   */
  navigate(path, state = {}) {
    const targetPath = localizedPath(path);
    if (targetPath === window.location.pathname) return;
    window.history.pushState(state, '', targetPath);
    this._handleRoute(targetPath);
  }

  /**
   * Replace current route
   * @param {string} path - Target path
   * @param {Object} state - History state
   */
  replace(path, state = {}) {
    const targetPath = localizedPath(path);
    window.history.replaceState(state, '', targetPath);
    this._handleRoute(targetPath);
  }

  /**
   * Go back
   */
  back() {
    window.history.back();
  }

  /**
   * Start the router
   * @param {string} rootSelector - Root element selector
   */
  start(rootSelector = '#app') {
    this._rootEl = document.querySelector(rootSelector);

    // Handle popstate
    window.addEventListener('popstate', () => {
      this._handleRoute(window.location.pathname);
    });

    // Handle link clicks
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;

      tracker.trackLinkClick(link);

      if (!link.matches('a[data-link]')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank') return;

      e.preventDefault();
      this.navigate(link.getAttribute('href'));
    });

    // Handle initial route
    this._handleRoute(window.location.pathname);
  }

  /**
   * Handle route change
   * @param {string} path - Route path
   */
  async _handleRoute(path) {
    const localeFromPath = getLocaleFromPath(path);
    if (localeFromPath) {
      setLocale(localeFromPath);
    } else {
      const cleanPath = stripLocaleFromPath(path);
      this.replace(cleanPath);
      return;
    }

    const routePath = stripLocaleFromPath(path);

    // Intercept auth and modal-only routes - redirect to home and open modals
    if (routePath === '/login' || routePath === '/register' || routePath === '/forgot-password') {
      this.replace('/');
      // Dynamically import and open the appropriate modal
      import('@features/auth/auth.modal.js').then(module => {
        if (routePath === '/login') {
          module.openLoginModal();
        } else if (routePath === '/register') {
          module.openRegisterModal();
        } else if (routePath === '/forgot-password') {
          module.openForgotPasswordModal();
        }
      });
      return;
    }

    // Intercept profile route - redirect to home and open profile modal
    if (routePath === '/profile') {
      this.replace('/');
      import('@features/profile/profile.modal.js').then(module => {
        module.openProfileModal();
      });
      return;
    }

    // Intercept paywall route - redirect to home and open paywall modal
    if (routePath === '/paywall') {
      this.replace('/');
      import('@features/paywall/paywall.modal.js').then(module => {
        module.openPaywallModal();
      });
      return;
    }

    // Intercept language route - redirect to home and open language modal
    if (routePath === '/language') {
      this.replace('/');
      import('@features/profile/language.modal.js').then(module => {
        module.openLanguageModal();
      });
      return;
    }

    // Intercept wizard route - redirect to home and open wizard modal
    if (routePath === '/wizard' || routePath.startsWith('/wizard/')) {
      this.replace('/');
      import('@features/wizard/wizard.modal.js').then(module => {
        module.openWizardModal();
      });
      return;
    }

    // Video modal path is URL-only for tracking; fallback to home on direct open.
    if (routePath === '/video') {
      this.replace('/');
      return;
    }

    const loader = this._routes.get(routePath);

    if (!loader) {
      // 404 - redirect to home
      this.replace('/');
      return;
    }

    this._currentRoute = routePath;
    eventBus.emit('route:change', { path: routePath });
    tracker.pageView(`${window.location.pathname}${window.location.search}${window.location.hash}`, {
      route_path: routePath,
      locale: localeFromPath,
    });

    try {
      const module = await loader();
      if (module.default && this._rootEl) {
        this._rootEl.innerHTML = '';
        const page = module.default();
        if (typeof page === 'string') {
          this._rootEl.innerHTML = page;
        } else if (page instanceof HTMLElement) {
          this._rootEl.appendChild(page);
        }
      }
    } catch (error) {
      console.error('Route loading error:', error);
      eventBus.emit('route:error', { path: routePath, error });
    }
  }

  /**
   * Get current route
   * @returns {string}
   */
  get currentRoute() {
    return this._currentRoute;
  }
}

// Singleton instance
export const router = new Router();
